import {createAsyncThunk} from '@reduxjs/toolkit';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import type {GridStorageTypes, StateProps} from '@Components/base-grid/base-grid-reducer';
import {deleteGridItems, updateGridItem} from '@Components/base-grid/base-grid-reducer';
import {castIdToStringForBaseGrid, findGridItem, GRID_ID, populateAudienceCustomFieldsFromAudienceResponse} from '@Libraries/email-marketing-campaigns-audience-library';
import type {GridEmailCampaignAudienceItemEmailsData} from '@Components/base-grid/components/grid-email-campaign-audience-item';
import type {SignupFormMailingListsResponse} from '@Components/email-signup-form-grid/email-signup-form-grid.types';
import type {RawCustomFieldValue} from '@Components/mailing-list-wizard/mailing-list-wizard.types';
import type {Audience, AudienceEmailState} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';
import {decrementMailingListCount} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience-reducer';
import {isMailingListDeleteForbidden, showMailingListDeletionForbiddenDialog} from '@Components/mailing-list-contacts/mailing-list-contacts-library';
import type {AjaxResponse} from '@Utils/ajax.util';

export interface GetEmailsForMailingListParams {
  idMailingList: number;
  limit: number;
  offset: number;
  searchTerm?: string;
  filters?: AudienceEmailState[];
  doFetchEmailsCount: boolean;
  deletedFilter?: number;
}

interface GetEmailsForCSVParams {
  idMailingList: number;
}

interface MailingListViewedParams {
  idMailingList: number;
}

export interface DeleteAudienceParams {
  idMailingList: number;
  audience: GridEmailCampaignAudienceItemEmailsData;
  index: number;
}

export interface UpdateMailingListNameParams {
  idMailingList: number;
  newName: string;
  currentName: string;
}

export interface UpdatePhoneNumberForAudienceInMailingListParams {
  idAudience: number;
  phoneNumber: string;
}

export interface UpdateFirstNameForAudienceInMailingListParams {
  idAudience: number;
  firstName: string;
}

export interface UpdateInfoForAudienceInMailingListParams {
  idAudience: number;
  fullName?: string;
  phoneNumber?: string;
}

interface UpdateContactFieldForAudienceInMailingListParams {
  idAudience: number;
  field: string; // The name of the field (e.g., 'fullName', 'phoneNumber')
  value: string;
}

interface UpdateMailingListNameInGridParams {
  itemId: string;
  newName: string;
}

interface DuplicateMailingListParams {
  idMailingList: string;
}

export interface DuplicateMailingListResponse {
  idMailingList: string;
}

export interface GetEmailsForMailingListResponse {
  idMailingList: number;
  name: string;
  audiences: Audience[];
  count: number;
  isSpam: boolean;
  lastViewed: number;
  isSharedInTeam: boolean;
  isSmart: boolean;
  idOwner: number;
}

interface GetEmailsForMailingListAjaxResponse extends Omit<GetEmailsForMailingListResponse, 'audiences'> {
  audiences: ResponseAudience[];
}

export interface ResponseAudience extends Omit<Audience, 'customFields'> {
  customFields?: RawCustomFieldValue[];
}

export const getEmailsForMailingList = createAsyncThunk('getEmailsForMailingList', async (data: GetEmailsForMailingListParams): Promise<GetEmailsForMailingListResponse> => {
  const response = (await window.PMW.readLocal('emailmarketing/getEmailsForAudience', data)) as GetEmailsForMailingListAjaxResponse;

  const {audiences} = response;
  const updatedAudiences = audiences.map(populateAudienceCustomFieldsFromAudienceResponse);
  return {...response, audiences: updatedAudiences};
});

export const duplicateMailingList = createAsyncThunk('duplicateMailingList', async (data: DuplicateMailingListParams): Promise<DuplicateMailingListResponse> => {
  showLoading('duplicatingMailingList', {text: 'Duplicating audience'});
  const response = (await window.PMW.readLocal('emailmarketing/duplicateMailingListV2', data)) as DuplicateMailingListResponse;
  hideLoading('duplicatingMailingList');
  return response;
});

export const deleteEmail = createAsyncThunk('deleteEmail', async (data: DeleteAudienceParams) => {
  return deleteEmailCall(data);
});

export const deleteEmailCall = (data: DeleteAudienceParams): Promise<void> => {
  return window.PMW.writeLocal('emailmarketing/deleteEmailFromMailingList', {
    idMailingList: data.idMailingList,
    email: data.audience.email,
  });
};

export const mailingListViewed = createAsyncThunk('mailingListViewed', async (data: MailingListViewedParams) => {
  return window.PMW.writeLocal('emailmarketing/updateMailingListLastViewed', {
    idMailingList: data.idMailingList,
  });
});

export const updateAllContactFieldsForMailingListAudience = createAsyncThunk(
  'updatePhoneNumberForMailingListAudience',
  async (data: {idAudience: number; audienceData: Partial<Audience>}) => {
    return window.PMW.writeLocal('emailmarketing/updateAllContactFieldsForMailingListAudience', {
      idAudience: data.idAudience,
      audienceData: data.audienceData,
    });
  }
);

export const updateContactFieldForMailingListAudience = createAsyncThunk(
  'updateContactFieldForMailingListAudience',
  async (data: UpdateContactFieldForAudienceInMailingListParams) => {
    return window.PMW.writeLocal('emailmarketing/updateContactFieldForMailingListAudience', {
      idAudience: data.idAudience,
      field: data.field, // The dynamic field being updated (e.g., 'fullName', 'phoneNumber', etc.)
      value: data.value, // The corresponding value for the field (e.g., the actual name or phone number)
    });
  }
);

export const updateMailingListName = createAsyncThunk('updateMailingListName', async (data: UpdateMailingListNameParams, {getState, dispatch, rejectWithValue}) => {
  const {grids} = getState() as {grids: StateProps};

  const grid = grids.gridsHashmap[GRID_ID];
  let response;

  try {
    updateMailingListNameInGrid(dispatch, grid, {
      itemId: castIdToStringForBaseGrid(data.idMailingList),
      newName: data.newName,
    });

    response = await window.PMW.writeLocal('emailmarketing/updateMailingListName', {
      idMailingList: data.idMailingList,
      name: data.newName,
    });
  } catch (err) {
    updateMailingListNameInGrid(dispatch, grid, {
      itemId: castIdToStringForBaseGrid(data.idMailingList),
      newName: data.currentName,
    });
    return rejectWithValue(err);
  }

  return response;
});

export const deleteMailingList = createAsyncThunk('deleteMailingList', async (data: GetEmailsForCSVParams, {getState, dispatch, rejectWithValue}) => {
  const {grids} = getState() as {grids: StateProps};

  const grid = grids.gridsHashmap[GRID_ID];
  showLoading('delete-list');

  try {
    const response = await window.PMW.writeLocal('emailmarketing/deleteMailingList', data);

    if (isMailingListDeleteForbidden(response as AjaxResponse)) {
      showMailingListDeletionForbiddenDialog();
      return;
    }

    const gridItem = findGridItem(grid, data.idMailingList);

    if (gridItem) {
      dispatch(
        deleteGridItems({
          gridId: GRID_ID,
          gridSelectionGroupId: GRID_ID,
          itemIdsToDelete: [gridItem.id],
        })
      );

      dispatch(decrementMailingListCount());
    }

    hideLoading('delete-list');
    return response;
  } catch (err) {
    rejectWithValue(err);
    hideLoading('delete-list');
  }
});

export const getUserMailingListsForSignUpForms = createAsyncThunk('getUserMailingListsForSignUpForms', async (): Promise<SignupFormMailingListsResponse> => {
  return (await window.PMW.readLocal('formmaker/getSignupFormMailingLists')) as SignupFormMailingListsResponse;
});

const updateMailingListNameInGrid = (dispatch: any, grid: GridStorageTypes, data: UpdateMailingListNameInGridParams) => {
  if (grid) {
    dispatch(
      updateGridItem({
        gridId: GRID_ID,
        itemId: data.itemId,
        changes: {
          name: data.newName,
        },
      })
    );
  }
};
